/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import { Coordinates } from '@karve.it/interfaces/locations';
import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';

import { BaseLocationFragment } from '../../generated/graphql.generated';
import { brandingFeature } from '../state/branding.store';

import { convertGoogleLocationToCreateLocationInput, findAddressComponent, getCoordinatesFromGeometry } from './google-helper.utils';
import { LoaderWrapper } from './loader-wrapper.service';

export interface GeocodeAddressInput {
  placeId?: string;
  formattedAddress?: string;
}

export interface GetGoogleAddressInput {
  placeId?: string;
  formattedAddress?: string;
  // https://developers.google.com/maps/documentation/javascript/places#place_details_requests
  fields?: string[];
}

export interface FindAddressComponentOptions {
    trimWhitespace: boolean;
}

export type AddressComponent = google.maps.GeocoderAddressComponent;
export type Address = google.maps.places.PlaceResult;

@Injectable({
  providedIn: 'root'
})
export class GoogleHelperService {

  placesService: google.maps.places.PlacesService;
  autoCompleteService: google.maps.places.AutocompleteService;
  streetViewService: google.maps.StreetViewService;
  loaded = new BehaviorSubject(false);


  apiKey: string;


  constructor(
    private store: Store,
    private loaderWrapper: LoaderWrapper,
  ) {}

  init() {
    const selectConfigs$ = this.store.select(brandingFeature.selectConfigs);

    selectConfigs$.subscribe((res) => {
      if (!res) { return; }

      const newApiKey = res['GOOGLEMAPS_PLACESAPIKEY'];
      if (newApiKey === this.apiKey) { return; }

      if (!newApiKey) {
        console.error(`No google maps API key provided`);
        return;
      }

      console.log('Google Maps API Key Updating...', newApiKey);
      this.apiKey = newApiKey;

      this.unloadGoogleMapsScript();

      // Reset services
      this.placesService = null;
      this.streetViewService = null;
      this.loaded.next(false);

      this.loaderWrapper.loader = new Loader({
        apiKey: this.apiKey,
        libraries: ['places'],
      });

      // Initialize the Google Services
      this.loaderWrapper.loader.importLibrary('places').then(() => {
        this.loaded.next(true);
        console.log('Google Maps Places library loaded successfully.');
        (globalThis as any).cmd.resolveAddress = (query: string) => this.resolveAddress(query);
      }).catch(e => {
        console.error('Failed to load Google Maps Places library', e);
        this.loaded.next(false);
      });
    });
  }

  private unloadGoogleMapsScript() {
    const scriptSelector = 'script[src*="maps.googleapis.com"]';
    const scripts = document.querySelectorAll(scriptSelector);
    scripts.forEach(script => {
      script.remove();
    });

    if ((window as any).google) {
      delete (window as any).google;
    }
    console.log('Previous Google Maps script unloaded.');
  }

  getAutoCompleteService() {
    if (this.autoCompleteService) { return this.autoCompleteService; }

    // The places service needs an element for the 'map' so we just create one but don't initialize the map
    this.autoCompleteService = new google.maps.places.AutocompleteService();

    return this.autoCompleteService;
  }

  getPlacesService() {
    if (this.placesService) { return this.placesService; }

    // The places service needs an element for the 'map' so we just create one but don't initialize the map
    this.placesService = new google.maps.places.PlacesService(document.createElement('div'));

    return this.placesService;
  }

  getStreetViewService() {
    if (this.streetViewService) { return this.streetViewService; }

    this.streetViewService = new google.maps.StreetViewService();

    return this.streetViewService;
  }

  convertGoogleLocationToCreateLocationInput(address: google.maps.places.PlaceResult) {
    return convertGoogleLocationToCreateLocationInput(address);
  }

  findAddressComponent(address: Address | google.maps.places.PlaceResult, type: string, options?: FindAddressComponentOptions){
    return findAddressComponent(address, type, options);
  }

  isValidGooglePlacesAddress = (addr): addr is Address => {
    if (!('address_components' in addr)) {
      return false;
    }

    // const street_number = this.findAddressComponent(addr, 'street_number');
    // const postal_code = this.findAddressComponent(addr, 'postal_code');
    // we need to have a postal code / street number to assign this location
    // if (!street_number || !postal_code) {
      // return false;
    // }

    return true;
  };

  /**
   * Get the Google Address value for a Location
   *
   * @param formattedAddress Is efffectively a search, Ideal use is to take an already formatted google address
   * @param fieldsOverride Provide different fields for the api instead of the default
   * @returns Google Address with the specified fields
   */
  getGoogleAddress(input: GetGoogleAddressInput): Promise<google.maps.places.PlaceResult> {
    return new Promise(async (resolve, _reject) => {
      // If we did not pass in a required field
      if (!input.placeId && !input.formattedAddress) {
        console.warn('Google Address Query Aborted, Please Provide placeId or formattedAddress');
        return resolve(null);
      }

      // If we don't have a place Id then retrieve it as it is needed for the details request
      if (!input.placeId) {
        input.placeId = await this.getPlaceIdForAddress(input.formattedAddress);

        // Not a valid place
        if (!input.placeId) {
          return resolve(null);
        }
      }

      const detailsRequest: google.maps.places.PlaceDetailsRequest = {
        placeId: input.placeId,
        fields: input.fields || ['address_component', 'formatted_address', 'geometry', 'name'],
      };

      this.getPlacesService().getDetails(detailsRequest, (detailsResult) => {
        resolve(detailsResult);
      });
    });
  }

  async resolveAddress(query: string) {
    const placeDetails = await this.getGoogleAddress({
      formattedAddress: query,
    });

    const locationBase = this.convertGoogleLocationToCreateLocationInput(placeDetails);

    return {
      placeDetails,
      locationBase,
    };
  }

  /**
   * Get the placeId for a location so it can be used in a details query
   *
   * @param address The Address for a location
   * @returns Google Place Id for that address
   */
  getPlaceIdForAddress(address: string): Promise<string> {
    const request = {
      query: address,
      fields: ['place_id'],
    };

    return new Promise((resolve, _reject) => {
      this.getPlacesService().findPlaceFromQuery(request, (results) => {
        // If it's not a valid place return null
        if (!results) { return resolve(null); }
        resolve(results[0].place_id);
      });
    });
  }

  /**
 * Fetch autocomplete suggestions for a given input
 *
 * @param input: google.maps.places.AutocompletionRequest The input string for autocomplete
 * @returns Array of autocomplete suggestions
 */
  async getAddressAutocomplete(request: google.maps.places.AutocompletionRequest): Promise<google.maps.places.AutocompleteResponse> {
    return this.getAutoCompleteService().getPlacePredictions(request);
  }

  /**
   * Get the Geocoordinates (Lat, Long) for a Google Location
   *
   * @param param The Place ID or formattedAddress based on the type
   * @param type The type of parameter you are providing
   * @returns The Geometry Result for a location
   */
  async geocodeAddress(input: GeocodeAddressInput) {
    return this.getGoogleAddress({
      ...input,
      fields: ['geometry']
    });
  }

  /**
   * Converts the geometry object from google into a our systems Coordinate type.
   *
   * @param result The Result from a geocodeAddress() call
   * @returns Coordinates formatted for our system
   */
  getCoordinatesFromGeometry(geometry: google.maps.places.PlaceGeometry): Coordinates {
    return getCoordinatesFromGeometry(geometry);
  }

  convertCoordinatesToLatLng(coords: Coordinates) {
    return {
      lat: coords.latitude,
      lng: coords.longitude,
    } as google.maps.LatLngLiteral;
  }

  /**
   * @returns Gooogle Maps API key
   */
  getKey(): string {
    return this.apiKey;
  }

  convertLocationToGoogleAddress(location: BaseLocationFragment) {

    const addressComponets: AddressComponent[] = [];

    this.insertComponent(addressComponets, 'postal_code', location.areaCode);
    this.insertComponent(addressComponets, 'country', location.country);
    this.insertComponent(addressComponets, 'administrative_area_level_1', location.countryJurisdiction);
    this.insertComponent(addressComponets, 'locality', location.city);

    return {
      formatted_address: location.addressLineOne,
      address_components: addressComponets,
    } as Partial<Address>;
  }

  insertComponent(components: AddressComponent[], componentType: string, val?: string) {
    if (!val) { return; }
    components.push({
      types: [ componentType ],
      long_name: val,
      short_name: val,
    });
  }
}
